// Media analytics tracker.
//
// Captures:
//   • view  — when the user opens a media player (every bucket)
//   • complete — fired when the user reaches the end (or >= 80%) of an item
//   • share — fired by Web Share API or "copy link" handlers
//
// Tracking semantics
// ──────────────────
// Anonymous users get a stable client-generated `anon_id` stored in
// localStorage. The backend's 60-minute dedupe window means a quick
// refresh won't inflate view counts. Each tracker call is fire-and-
// forget — failures are swallowed so analytics never break playback.
import api from "./api";

const ANON_ID_KEY = "hw:analytics:anon-id";

function getAnonId() {
  try {
    let id = window.localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      // crypto.randomUUID is available in all modern browsers; fall back
      // to a short random hex string just in case.
      id = (window.crypto?.randomUUID && window.crypto.randomUUID())
        || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch (_) {
    return "anon";
  }
}

// In-session per-(bucket, item) dedupe for views, so a card we open
// twice in one session only counts once on the client. The server's
// 60-min dedupe is the authoritative gate; this just keeps request
// volume down.
const SESSION_VIEWED = new Set();
const SESSION_COMPLETED = new Set();

export function trackView(bucket, item) {
  if (!bucket || !item) return;
  const itemId = String(item.id ?? item.item_id ?? "");
  if (!itemId) return;
  const key = `${bucket}:${itemId}`;
  if (SESSION_VIEWED.has(key)) return;
  SESSION_VIEWED.add(key);
  api
    .post("/analytics/view", { bucket, item_id: itemId, anon_id: getAnonId() })
    .catch(() => SESSION_VIEWED.delete(key));
}

export function trackComplete(bucket, item) {
  if (!bucket || !item) return;
  const itemId = String(item.id ?? item.item_id ?? "");
  if (!itemId) return;
  const key = `${bucket}:${itemId}`;
  if (SESSION_COMPLETED.has(key)) return;
  SESSION_COMPLETED.add(key);
  api
    .post("/analytics/complete", { bucket, item_id: itemId, anon_id: getAnonId() })
    .catch(() => SESSION_COMPLETED.delete(key));
}

export function trackShare(bucket, item, channel = "link") {
  if (!bucket || !item) return;
  const itemId = String(item.id ?? item.item_id ?? "");
  if (!itemId) return;
  api
    .post("/analytics/share", { bucket, item_id: itemId, channel, anon_id: getAnonId() })
    .catch(() => {});
}

export async function fetchCounts(bucket, itemIds) {
  if (!bucket || !Array.isArray(itemIds) || itemIds.length === 0) return {};
  try {
    const r = await api.post("/analytics/counts", { bucket, item_ids: itemIds });
    return r.data?.counts || {};
  } catch (_) {
    return {};
  }
}

// "1.2k views" formatting — YouTube-style. 0-999 stays plain, then
// 1.2k → 12k → 123k → 1.2m. Avoids decimals when the rounded value
// is over 10 (e.g. "12k" not "12.0k") for cleaner glance reading.
export function formatViews(n) {
  const num = Number(n) || 0;
  if (num < 1000) return String(num);
  if (num < 10_000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  if (num < 1_000_000) return `${Math.round(num / 1000)}k`;
  if (num < 10_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  return `${Math.round(num / 1_000_000)}m`;
}
